import { Card, Typography } from '@material-ui/core'
import { cloneElement } from 'react'
import {
  ArrayField,
  ArrayInput,
  Datagrid,
  Error,
  Labeled,
  Loading,
  NumberInput,
  required,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  useCreateController,
  useGetOne,
} from 'react-admin'
import { useLocation } from 'react-router-dom'

import { useFeeItemTypes } from 'helpers/useFeeItemTypes'
import { getRequest } from '../../api/common'
import { STATEMENT_LINE_ITEM_TYPE_CHOICES, StatementType } from '../../helpers/constants'
import {
  validateStatementAmounts,
  validateStatementItems,
  validateStatementTotalCap,
} from '../../helpers/validators'
import { LabeledTypographyField } from '../Fields'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

export function getFeeItemTypes() {
  const url = `/api/v1/console/get-fee-item-types`
  return getRequest(url).then((response) => {
    return response['fee_item_types'].map((type: string) => ({
      id: type,
      name: type,
    }))
  })
}

const CreateWrapper = (props: any) => {
  const { basePath, record, resource, save, saving, version } = useCreateController(props)

  const redirect = 'show'
  const { data: job, loading, error } = useGetOne('jobs', props.match.params.id)
  const isAccessorial = props.isAccessorial
  if (loading) {
    return <Loading />
  }
  if (error) {
    return <Error error={error} />
  }

  let originalStatement = job?.statements[0]
  if (!originalStatement) {
    return (
      <div>
        <Typography component="span" variant="h4">
          Oops, no original job statement found!
        </Typography>
      </div>
    )
  }
  let originalSubtotal = originalStatement.subtotal
  let originalTotal = originalStatement.total

  let header = isAccessorial ? 'Add Accessorial Statement' : 'Job Payment Correction'
  let subHeader = isAccessorial
    ? "Add an accessorial statement to this job. No fees will be applied to the statement's line items."
    : 'To correct an already-processed job statement, create a new statement using the form below.'
  let statementName = isAccessorial ? 'Accessorial statement' : 'Correction statement'
  return (
    <div>
      <Typography component="span" variant="h2">
        {header}
      </Typography>
      <br />
      <br />
      <Typography component="span" variant="body1">
        {subHeader}
      </Typography>
      <br />
      <br />
      <br />
      <Card style={{ padding: '15px' }}>
        <Typography component="span" variant="h5">
          Original statement
        </Typography>
        <br />
        <LabeledTypographyField label="Job" value={job?.human_id} variant="body1" />
        <br />
        <LabeledTypographyField label="Team" value={job?.team.name} variant="body1" />
        <br />
        <Labeled label="Line items">
          <ArrayField record={originalStatement} source="line_items">
            <Datagrid>
              <TextField source="amount" />
              <TextField source="type" />
              <TextField source="description" />
            </Datagrid>
          </ArrayField>
        </Labeled>
        <br />
        <Labeled label="Fee items">
          <ArrayField record={originalStatement} source="fee_items">
            <Datagrid>
              <TextField source="amount" />
              <TextField source="description" />
              <TextField source="type" />
            </Datagrid>
          </ArrayField>
        </Labeled>
        <br />
        <LabeledTypographyField
          label="Original statement subtotal"
          value={originalSubtotal}
          variant="body1"
        />
        <br />
        <LabeledTypographyField label="Original statement total" value={originalTotal} variant="body1" />
      </Card>
      <br />
      <br />
      <br />
      <Card style={{ padding: '15px' }}>
        <Typography component="span" variant="h5">
          {statementName}
        </Typography>
        {cloneElement(props.children, {
          basePath,
          record,
          redirect,
          resource,
          save,
          saving,
          version,
        })}
      </Card>
    </div>
  )
}

const getInitialValues = ({ match }: any, isAccessorial: boolean) => {
  let { params } = match
  let values = {
    job: params.id,
    type: StatementType.JobCorrection,
    zero_fees: isAccessorial,
  }
  return values
}

const StatementCreate = (props: any) => {
  const query = useQuery()
  const isAccessorial = query.get('isAccessorial') ? true : false
  const { feeItemTypes } = useFeeItemTypes()
  return (
    <CreateWrapper
      basePath="/statements"
      resource="statements"
      redirect="show"
      isAccessorial={isAccessorial}
      {...props}
    >
      <SimpleForm initialValues={getInitialValues(props, isAccessorial)} validate={validateStatementItems}>
        <TextInput source="description" validate={required()} />
        <ArrayInput source="line_items">
          <SimpleFormIterator>
            <SelectInput
              source="type"
              choices={STATEMENT_LINE_ITEM_TYPE_CHOICES}
              label="Line item type"
              validate={required()}
            />
            <NumberInput
              source="amount"
              label="Amount"
              validate={[validateStatementAmounts, validateStatementTotalCap]}
            />
            <TextInput source="description" label="Description" />
          </SimpleFormIterator>
        </ArrayInput>
        <ArrayInput source="fee_items">
          <SimpleFormIterator>
            <NumberInput
              source="amount"
              label="Amount"
              validate={[validateStatementAmounts, validateStatementTotalCap]}
            />
            <TextInput source="description" label="Description" validate={required()} />
            <SelectInput label="Type" source="type" choices={feeItemTypes} alwaysOn />
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </CreateWrapper>
  )
}

export default StatementCreate
